import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { Plus, X, Trash2, ChevronDown, ChevronUp, AlertTriangle, Banknote, Smartphone, Check, PackageX, Camera, Image, Search } from 'lucide-react';
import api from '../../api';
import VendorProductWizard from './VendorProductWizard';

const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const parseUTC = (iso) => {
  if (!iso) return null;
  const s = typeof iso === 'string' ? iso : iso;
  if (!s.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s.replace(' ', 'T') + 'Z');
  return new Date(s);
};
const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = parseUTC(iso);
  return d ? d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
};

const STATUS_MAP = {
  paid: { label: 'Pagado', color: '#4CAF50', bg: 'rgba(76,175,80,0.12)' },
  partial: { label: 'Parcial', color: '#FF9800', bg: 'rgba(255,152,0,0.12)' },
  pending: { label: 'Pendiente', color: '#FF5252', bg: 'rgba(255,82,82,0.12)' },
};

const PAY_PERIODS = [
  { key: 'daily', label: 'Hoy' },
  { key: 'weekly', label: 'Semana' },
  { key: 'monthly', label: 'Mes' },
  { key: 'yearly', label: 'Año' },
  { key: 'custom', label: 'Rango' },
];

const EMPTY_ITEM = { description: '', quantity: '', unit_cost: '' };

export default function Purchases() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isVendedor = user?.business_type === 'Vendedor';
  const [activeTab, setActiveTab] = useState('purchases');
  const [purchases, setPurchases] = useState([]);
  const [providers, setProviders] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [debtSummary, setDebtSummary] = useState({ total_remaining_debt: 0, purchase_count: 0 });
  const [expandedId, setExpandedId] = useState(null);
  const [showVendorWizard, setShowVendorWizard] = useState(false);

  // Provider payments (Pagos a Proveedores) tab
  const [payPeriod, setPayPeriod] = useState('monthly');
  const [payMonthValue, setPayMonthValue] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [payFromDate, setPayFromDate] = useState('');
  const [payToDate, setPayToDate] = useState('');
  const [paySearch, setPaySearch] = useState('');
  const [paymentsSummary, setPaymentsSummary] = useState({ total_paid: 0, payments: [], count: 0 });
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Write-off state
  const [showWriteoffModal, setShowWriteoffModal] = useState(false);
  const [writeoffTarget, setWriteoffTarget] = useState(null);
  const [writeoffForm, setWriteoffForm] = useState({ quantity: '', reason: '' });
  const [writingOff, setWritingOff] = useState(false);
  const WRITEOFF_REASONS = ['Producto defectuoso', 'Vencido/Caducado', 'Pérdida/Robo', 'Daño en depósito', 'Otro'];

  // Create purchase modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    provider_id: '', items: [{ ...EMPTY_ITEM }], notes: '', status: 'pending', paid_amount: '', photos: [], payment_method: '', shipping_cost: '',
  });
  const [formNewProvider, setFormNewProvider] = useState('');
  const [showNewProviderInline, setShowNewProviderInline] = useState(false);
  const photoInputRef = useRef(null);
  const payPhotoInputRef = useRef(null);

  // Provider modal
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [providerForm, setProviderForm] = useState({ name: '', phone: '', notes: '' });
  const [editingProvider, setEditingProvider] = useState(null);
  const [savingProvider, setSavingProvider] = useState(false);
  const [deleteProvider, setDeleteProvider] = useState(null);

  // Payment modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', payment_method: '', photo: null });
  const [paying, setPaying] = useState(false);
  const [payAlert, setPayAlert] = useState('');

  // Payment history
  const [paymentHistory, setPaymentHistory] = useState({});  // { purchaseId: [...payments] }

  // Delete purchase confirm
  const [deletePurchase, setDeletePurchase] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Image lightbox
  const [viewingPhoto, setViewingPhoto] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [pRes, provRes, debtRes, stockRes] = await Promise.all([
        api.get(`/purchases/${user.id}`),
        api.get(`/providers/${user.id}`),
        api.get(`/provider-debt/${user.id}`),
        api.get(`/stock/${user.id}`),
      ]);
      setPurchases(pRes.data);
      setProviders(provRes.data);
      setDebtSummary(debtRes.data);
      setStockItems(stockRes.data);
    } catch (e) { console.error(e); }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const loadProviderPayments = useCallback(async () => {
    if (!user?.id) return;
    if (payPeriod === 'custom' && (!payFromDate || !payToDate)) return;
    setLoadingPayments(true);
    try {
      const params = { period: payPeriod };
      if (payPeriod === 'monthly' && payMonthValue) {
        const [y, m] = payMonthValue.split('-');
        params.month = parseInt(m, 10);
        params.year = parseInt(y, 10);
      }
      if (payPeriod === 'custom') { params.from_date = payFromDate; params.to_date = payToDate; }
      if (paySearch.trim()) params.search = paySearch.trim();
      const r = await api.get(`/provider-payments/${user.id}`, { params });
      setPaymentsSummary(r.data);
    } catch (e) { console.error(e); }
    finally { setLoadingPayments(false); }
  }, [user?.id, payPeriod, payMonthValue, payFromDate, payToDate, paySearch]);

  useEffect(() => {
    if (activeTab !== 'payments') return;
    const t = setTimeout(() => loadProviderPayments(), 300);
    return () => clearTimeout(t);
  }, [activeTab, loadProviderPayments]);

  // ──────────────────── PURCHASE CRUD ────────────────────

  const openCreate = () => {
    setPurchaseForm({ provider_id: providers[0]?.id || '', items: [{ ...EMPTY_ITEM }], notes: '', status: 'pending', paid_amount: '', photos: [], payment_method: '', shipping_cost: '' });
    setFormNewProvider('');
    setShowNewProviderInline(false);
    setShowCreateModal(true);
  };

  const addItem = () => setPurchaseForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (i) => setPurchaseForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, val) => setPurchaseForm(f => {
    const items = [...f.items];
    items[i] = { ...items[i], [field]: val };
    return { ...f, items };
  });

  const calcTotal = () => purchaseForm.items.reduce((s, it) => {
    const q = parseFloat(it.quantity) || 0;
    const c = parseFloat(it.unit_cost) || 0;
    return s + q * c;
  }, 0) + (parseFloat(purchaseForm.shipping_cost) || 0);

  const createInlineProvider = async () => {
    if (!formNewProvider.trim()) return;
    setSavingProvider(true);
    try {
      const r = await api.post('/providers', { business_user_id: user.id, name: formNewProvider.trim() });
      setProviders(prev => [...prev, r.data]);
      setPurchaseForm(f => ({ ...f, provider_id: r.data.id }));
      setFormNewProvider('');
      setShowNewProviderInline(false);
    } catch (e) { alert('Error al crear proveedor'); }
    finally { setSavingProvider(false); }
  };

  const addPurchasePhoto = (e) => {
    const files = Array.from(e.target.files).slice(0, 5 - purchaseForm.photos.length);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => setPurchaseForm(f => ({ ...f, photos: [...f.photos, ev.target.result] }));
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePurchasePhoto = (i) => setPurchaseForm(f => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const savePurchase = async (e) => {
    e.preventDefault();
    if (!purchaseForm.provider_id) { alert('Seleccioná un proveedor'); return; }
    const validItems = purchaseForm.items.filter(i => i.description.trim() && parseFloat(i.quantity) > 0 && parseFloat(i.unit_cost) > 0);
    if (validItems.length === 0) { alert('Agregá al menos un ítem con descripción, cantidad y costo'); return; }
    if ((purchaseForm.status === 'paid' || purchaseForm.status === 'partial') && !purchaseForm.payment_method) {
      alert('Seleccioná la forma de pago (Efectivo o Transferencia)');
      return;
    }
    setSaving(true);
    try {
      await api.post('/purchases', {
        business_user_id: user.id,
        provider_id: purchaseForm.provider_id,
        items: validItems.map(i => ({ description: i.description.trim(), quantity: parseFloat(i.quantity), unit_cost: parseFloat(i.unit_cost) })),
        notes: purchaseForm.notes || null,
        status: purchaseForm.status,
        paid_amount: purchaseForm.status === 'partial' ? (parseFloat(purchaseForm.paid_amount) || 0) : 0,
        photos: purchaseForm.photos,
        payment_method: (purchaseForm.status === 'paid' || purchaseForm.status === 'partial') ? purchaseForm.payment_method : null,
        shipping_cost: parseFloat(purchaseForm.shipping_cost) || 0,
      });
      setShowCreateModal(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error al registrar compra'); }
    finally { setSaving(false); }
  };

  // ──────────────────── PAYMENT ────────────────────

  const openPayModal = (purchase) => {
    setPayTarget(purchase);
    setPayForm({ amount: String(purchase.remaining_debt.toFixed(2)), payment_method: '', photo: null });
    setPayAlert('');
    setShowPayModal(true);
  };

  const addPayPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setPayForm(f => ({ ...f, photo: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const loadPaymentHistory = async (purchaseId) => {
    if (paymentHistory[purchaseId]) return; // already loaded
    try {
      const r = await api.get(`/purchase-payments/${purchaseId}`);
      setPaymentHistory(prev => ({ ...prev, [purchaseId]: r.data }));
    } catch { console.error('Error loading payment history'); }
  };

  const confirmPayment = async () => {
    if (!payForm.payment_method) { setPayAlert('Seleccioná una forma de pago'); return; }
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) { setPayAlert('Ingresá un monto válido'); return; }
    setPaying(true);
    try {
      await api.post(`/purchases/${payTarget.id}/payment`, {
        amount,
        payment_method: payForm.payment_method,
        photo: payForm.photo || null,
      });
      setShowPayModal(false);
      // Refresh history for this purchase
      setPaymentHistory(prev => { const n = { ...prev }; delete n[payTarget.id]; return n; });
      load();
    } catch (err) { setPayAlert(err.response?.data?.detail || 'Error al registrar pago'); }
    finally { setPaying(false); }
  };

  // ──────────────────── PROVIDERS CRUD ────────────────────

  const openProviderModal = (p = null) => {
    setEditingProvider(p);
    setProviderForm({ name: p?.name || '', phone: p?.phone || '', notes: p?.notes || '' });
    setShowProviderModal(true);
  };

  const saveProvider = async (e) => {
    e.preventDefault();
    if (!providerForm.name.trim()) { alert('Ingresá el nombre del proveedor'); return; }
    setSavingProvider(true);
    try {
      if (editingProvider) {
        await api.put(`/providers/${editingProvider.id}`, providerForm);
      } else {
        await api.post('/providers', { business_user_id: user.id, ...providerForm });
      }
      setShowProviderModal(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSavingProvider(false); }
  };

  const confirmDeleteProvider = async () => {
    try {
      await api.delete(`/providers/${deleteProvider.id}`);
      setDeleteProvider(null);
      load();
    } catch { alert('Error al eliminar'); }
  };

  const confirmDeletePurchase = async () => {
    setDeleting(true);
    try {
      await api.delete(`/purchases/${deletePurchase.id}`);
      setDeletePurchase(null);
      load();
    } catch { alert('Error al eliminar'); }
    finally { setDeleting(false); }
  };

  // ── Write-off ─────────────────────────────────────
  const openWriteoff = (item) => {
    setWriteoffTarget(item);
    setWriteoffForm({ quantity: '', reason: '' });
    setShowWriteoffModal(true);
  };

  const confirmWriteoff = async () => {
    if (!writeoffForm.quantity || parseInt(writeoffForm.quantity) < 1) { alert('Ingresá una cantidad válida'); return; }
    if (!writeoffForm.reason) { alert('Seleccioná un motivo'); return; }
    setWritingOff(true);
    try {
      await api.post(`/stock/${writeoffTarget.id}/writeoff`, { quantity: parseInt(writeoffForm.quantity), reason: writeoffForm.reason });
      setShowWriteoffModal(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error al dar de baja'); }
    finally { setWritingOff(false); }
  };

  const purchaseTotal = calcTotal();

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title" style={{ flex: 1 }}>Compras / Proveedores</h1>
        <button
          data-testid="new-purchase-btn"
          className="btn-primary"
          style={{ fontSize: '13px', padding: '6px 14px' }}
          onClick={() => isVendedor ? setShowVendorWizard(true) : openCreate()}
        >
          + Nueva
        </button>
      </div>

      {/* Debt summary */}
      {debtSummary.total_remaining_debt > 0 && (
        <div style={{
          margin: '0 16px 12px',
          background: 'rgba(255,82,82,0.08)',
          border: '1px solid rgba(255,82,82,0.3)',
          borderRadius: 12,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <AlertTriangle size={20} color="#ff5252" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#ff5252', fontSize: '13px' }}>
              Deuda pendiente con proveedores
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: 1 }}>
              {debtSummary.purchase_count} compra(s) sin saldar
            </div>
          </div>
          <span style={{ fontWeight: 800, color: '#ff5252', fontSize: '16px' }}>
            {fmt(debtSummary.total_remaining_debt)}
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="period-tabs" style={{ marginBottom: 0 }}>
        <button className={`period-tab ${activeTab === 'purchases' ? 'active' : ''}`} onClick={() => setActiveTab('purchases')}>Compras</button>
        <button className={`period-tab ${activeTab === 'providers' ? 'active' : ''}`} onClick={() => setActiveTab('providers')}>Proveedores</button>
        <button data-testid="tab-debts" className={`period-tab ${activeTab === 'debts' ? 'active' : ''}`} onClick={() => setActiveTab('debts')}>Deuda a Proveedores</button>
        <button data-testid="tab-payments" className={`period-tab ${activeTab === 'payments' ? 'active' : ''}`} onClick={() => setActiveTab('payments')}>Pagos a Proveedores</button>
        <button className={`period-tab ${activeTab === 'writeoffs' ? 'active' : ''}`} onClick={() => setActiveTab('writeoffs')}>Bajas</button>
      </div>

      <div className="content-scroll">
        {/* ── PAGOS A PROVEEDORES TAB ── */}
        {activeTab === 'payments' && (
          <>
            <div className="period-tabs" style={{ marginBottom: 12 }}>
              {PAY_PERIODS.map(p => (
                <button key={p.key} data-testid={`pay-period-${p.key}`} className={`period-tab ${payPeriod === p.key ? 'active' : ''}`} onClick={() => setPayPeriod(p.key)}>{p.label}</button>
              ))}
            </div>

            {payPeriod === 'monthly' && (
              <div className="form-group">
                <label className="label">Mes</label>
                <input className="input" type="month" data-testid="pay-month-input" value={payMonthValue} onChange={e => setPayMonthValue(e.target.value)} />
              </div>
            )}

            {payPeriod === 'custom' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label className="label">Desde</label>
                  <input className="input" type="date" data-testid="pay-range-from" value={payFromDate} onChange={e => setPayFromDate(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="label">Hasta</label>
                  <input className="input" type="date" data-testid="pay-range-to" value={payToDate} onChange={e => setPayToDate(e.target.value)} />
                </div>
              </div>
            )}

            <div style={{ position: 'relative', marginBottom: 14 }}>
              <Search size={16} color="var(--text2)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                className="input"
                data-testid="pay-search-input"
                placeholder="Buscar por proveedor..."
                value={paySearch}
                onChange={e => setPaySearch(e.target.value)}
                style={{ paddingLeft: 36 }}
              />
            </div>

            <div style={{ textAlign: 'center', padding: '16px', background: 'var(--surface)', borderRadius: 12, marginBottom: 16 }}>
              <div style={{ fontSize: '12px', color: 'var(--text2)' }}>Total pagado a proveedores</div>
              <div data-testid="total-paid-providers" style={{ fontSize: '28px', fontWeight: 800, color: '#27AE60', marginTop: 4 }}>{fmt(paymentsSummary.total_paid)}</div>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: 2 }}>{paymentsSummary.count} pago(s) en el período · No afecta la Ganancia Neta</div>
            </div>

            {loadingPayments ? (
              <div className="spinner" style={{ margin: '30px auto' }} />
            ) : paymentsSummary.payments.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">💳</span>
                <p className="empty-text">Sin pagos en este período</p>
              </div>
            ) : (
              paymentsSummary.payments.map(pay => (
                <div key={pay.id} className="card-row" data-testid={`payment-row-${pay.id}`}>
                  <div className="card-row-info">
                    <div style={{ fontWeight: 700, fontSize: '14px' }}>{pay.provider_name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {fmtDate(pay.created_at)} · {pay.payment_method === 'efectivo' ? <Banknote size={12} /> : <Smartphone size={12} />} {pay.payment_method === 'efectivo' ? 'Efectivo' : 'Transferencia'}
                    </div>
                  </div>
                  <span style={{ fontWeight: 800, color: '#27AE60', fontSize: '16px' }}>{fmt(pay.amount)}</span>
                </div>
              ))
            )}
          </>
        )}

        {/* ── DEUDA A PROVEEDORES TAB ── */}
        {activeTab === 'debts' && (
          <>
            <div style={{ background: 'rgba(39,174,96,0.07)', border: '1px solid rgba(39,174,96,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '13px', color: 'var(--text2)' }}>
              Deudas pendientes o parciales con tus proveedores. Registrar un pago acá NO se contabiliza como gasto operativo (el costo del producto ya se descontó al vender).
            </div>
            {purchases.filter(p => p.status !== 'paid').length === 0 && (
              <div className="empty-state">
                <span className="empty-icon">✅</span>
                <p className="empty-text">Sin deudas pendientes</p>
                <p className="empty-subtext">Estás al día con todos tus proveedores</p>
              </div>
            )}
            {purchases.filter(p => p.status !== 'paid').map(p => {
              const st = STATUS_MAP[p.status] || STATUS_MAP.pending;
              return (
                <div key={p.id} className="card-row" data-testid={`debt-row-${p.id}`}>
                  <div className="card-row-info">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: '15px' }}>{p.provider_name}</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: st.color, background: st.bg, padding: '2px 8px', borderRadius: 4 }}>{st.label}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{fmtDate(p.created_at)} · Total: {fmt(p.total_cost)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text2)' }}>Debe</div>
                      <div style={{ fontWeight: 800, color: '#ff5252', fontSize: '16px' }}>{fmt(p.remaining_debt)}</div>
                    </div>
                    <button
                      data-testid={`debt-pay-btn-${p.id}`}
                      onClick={() => openPayModal(p)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, background: 'linear-gradient(135deg, #4CAF50, #66BB6A)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}
                    >
                      <Check size={14} /> Pagar
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* ── COMPRAS TAB ── */}
        {activeTab === 'purchases' && (
          <>
            {purchases.length === 0 && (
              <div className="empty-state">
                <span className="empty-icon">🛒</span>
                <p className="empty-text">No hay compras registradas</p>
                <p className="empty-subtext">Usá "+ Nueva" para registrar una compra a un proveedor</p>
              </div>
            )}
            {purchases.map(p => {
              const st = STATUS_MAP[p.status] || STATUS_MAP.pending;
              const expanded = expandedId === p.id;
              return (
                <div key={p.id} className="card" data-testid={`purchase-card-${p.id}`} style={{ marginBottom: 10, padding: 0, overflow: 'hidden' }}>
                  <button
                    style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '14px 16px', textAlign: 'left' }}
                    onClick={() => {
                      const newExpanded = expanded ? null : p.id;
                      setExpandedId(newExpanded);
                      // Auto-load payment history when expanding a purchase that has payments
                      if (newExpanded && p.paid_amount > 0 && !paymentHistory[p.id]) {
                        loadPaymentHistory(p.id);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: '15px' }}>{p.provider_name}</span>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: st.color, background: st.bg, padding: '2px 8px', borderRadius: 4 }}>{st.label}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{fmtDate(p.created_at)} · {p.items.length} ítem(s)</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                        <div style={{ fontWeight: 800, fontSize: '16px' }}>{fmt(p.total_cost)}</div>
                        {p.remaining_debt > 0 && (
                          <div style={{ fontSize: '11px', color: '#ff5252', fontWeight: 600 }}>Debe: {fmt(p.remaining_debt)}</div>
                        )}
                      </div>
                      <div style={{ marginLeft: 10, color: 'var(--text2)' }}>
                        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px' }}>
                      {/* Items table */}
                      <div style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: 8 }}>Ítems de la compra:</div>
                      {p.items.map((it, i) => (
                        <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--text)' }}>
                            <span>{it.description} × {it.quantity}</span>
                            <span style={{ fontWeight: 600 }}>{fmt(it.quantity * it.unit_cost)} <span style={{ color: 'var(--text2)', fontWeight: 400 }}>({fmt(it.unit_cost)}/u)</span></span>
                          </div>
                          {it.shipping_per_unit > 0 && (
                            <div style={{ fontSize: '11px', color: '#FF9800', marginTop: 2 }}>
                              + Envío: {fmt(it.shipping_per_unit)}/u → Costo real: <strong>{fmt(it.real_unit_cost)}/u</strong>
                            </div>
                          )}
                        </div>
                      ))}
                      {p.shipping_cost > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px', color: 'var(--text2)', borderBottom: '1px solid var(--border)' }}>
                          <span>Gasto de envío</span>
                          <span style={{ fontWeight: 600, color: 'var(--text)' }}>{fmt(p.shipping_cost)}</span>
                        </div>
                      )}
                      {p.notes && (
                        <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 8, fontStyle: 'italic' }}>Nota: {p.notes}</div>
                      )}
                      {/* Invoice photos */}
                      {p.photos && p.photos.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Camera size={12} /> Facturas / comprobantes:
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {p.photos.map((photo, i) => (
                              <button key={i} onClick={() => setViewingPhoto(photo)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'zoom-in' }}>
                                <img src={photo} alt={`Factura ${i + 1}`} style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 8 }}>
                        <div>
                          <span style={{ fontSize: '12px', color: 'var(--text2)' }}>Pagado: </span>
                          <span style={{ fontWeight: 700, color: '#4CAF50' }}>{fmt(p.paid_amount)}</span>
                          {p.remaining_debt > 0 && (
                            <span style={{ fontSize: '12px', color: 'var(--text2)', marginLeft: 8 }}>· Pendiente: <span style={{ color: '#ff5252', fontWeight: 700 }}>{fmt(p.remaining_debt)}</span></span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {p.remaining_debt > 0.01 && (
                            <button
                              data-testid={`pay-purchase-${p.id}`}
                              onClick={() => openPayModal(p)}
                              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, background: 'linear-gradient(135deg, #4CAF50, #66BB6A)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}
                            >
                              <Check size={14} /> Pagar
                            </button>
                          )}
                          <button
                            data-testid={`delete-purchase-${p.id}`}
                            onClick={() => setDeletePurchase(p)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 4 }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Payment history */}
                      <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                        {p.paid_amount > 0 ? (
                          <>
                            <div style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: 8, fontWeight: 600 }}>
                              Historial de pagos {paymentHistory[p.id] ? `(${paymentHistory[p.id].length})` : ''}
                            </div>
                            {!paymentHistory[p.id] && (
                              <button
                                data-testid={`payment-history-btn-${p.id}`}
                                onClick={() => loadPaymentHistory(p.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: '12px', fontWeight: 600, padding: 0 }}
                              >
                                Ver historial ▾
                              </button>
                            )}
                            {paymentHistory[p.id] && (
                              <div>
                                {paymentHistory[p.id].length === 0 && (
                                  <div style={{ fontSize: '12px', color: 'var(--text2)', fontStyle: 'italic' }}>Sin pagos registrados en el historial</div>
                                )}
                                {paymentHistory[p.id].map((pay, idx) => (
                                  <div key={pay.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#4CAF50' }}>{fmt(pay.amount)}</div>
                                      <div style={{ fontSize: '11px', color: 'var(--text2)' }}>
                                        {pay.payment_method === 'efectivo' ? '💵 Efectivo' : '📱 Transferencia'} · {fmtDate(pay.created_at)}
                                      </div>
                                    </div>
                                    {pay.photo && (
                                      <button onClick={() => setViewingPhoto(pay.photo)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'zoom-in', flexShrink: 0 }}>
                                        <img src={pay.photo} alt="Comprobante" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{ fontSize: '12px', color: 'var(--text2)', fontStyle: 'italic' }}>
                            Sin pagos registrados
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* ── PROVEEDORES TAB ── */}
        {activeTab === 'providers' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <button
                data-testid="add-provider-btn"
                className="btn-primary"
                style={{ fontSize: '13px', padding: '7px 14px' }}
                onClick={() => openProviderModal()}
              >
                + Nuevo Proveedor
              </button>
            </div>
            {providers.length === 0 && (
              <div className="empty-state">
                <span className="empty-icon">🤝</span>
                <p className="empty-text">No hay proveedores registrados</p>
              </div>
            )}
            {providers.map(prov => {
              const provPurchases = purchases.filter(p => p.provider_id === prov.id);
              const totalDebt = provPurchases.filter(p => p.status !== 'paid').reduce((s, p) => s + p.remaining_debt, 0);
              return (
                <div key={prov.id} className="card-row" data-testid={`provider-card-${prov.id}`}>
                  <div className="card-row-info">
                    <div className="card-row-title">{prov.name}</div>
                    {prov.phone && <div className="card-row-detail">📞 {prov.phone}</div>}
                    <div className="card-row-detail">{provPurchases.length} compra(s)</div>
                    {totalDebt > 0 && (
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#ff5252', marginTop: 2 }}>Debe: {fmt(totalDebt)}</div>
                    )}
                    {totalDebt === 0 && provPurchases.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#4CAF50', fontWeight: 600 }}>Al día ✓</div>
                    )}
                  </div>
                  <div className="card-row-actions">
                    <button className="icon-btn-sm" onClick={() => openProviderModal(prov)}>✏️</button>
                    <button className="icon-btn-sm danger" onClick={() => setDeleteProvider(prov)}>🗑️</button>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* ── BAJAS TAB ── */}
        {activeTab === 'writeoffs' && (
          <>
            <div style={{ background: 'rgba(255,82,82,0.07)', border: '1px solid rgba(255,82,82,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '12px', color: 'var(--text2)', lineHeight: 1.5 }}>
              <span style={{ color: '#ff5252', fontWeight: 700 }}>Baja de inventario</span> — Registrá productos defectuosos, vencidos o perdidos para mantener el stock actualizado.
            </div>
            {stockItems.length === 0 && (
              <div className="empty-state">
                <span className="empty-icon">📦</span>
                <p className="empty-text">No hay items en el stock</p>
              </div>
            )}
            {stockItems.map(s => (
              <div key={s.id} className="card-row" data-testid={`stock-writeoff-${s.id}`}>
                <div className="card-row-info">
                  <div className="card-row-title" style={{ fontSize: '15px' }}>{s.description}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text2)' }}>Stock actual:</span>
                    <span style={{ fontWeight: 700, color: s.quantity <= 3 ? '#ff5252' : 'var(--accent)', fontSize: '14px' }}>{s.quantity} unidades</span>
                    {s.unit_cost && <span style={{ fontSize: '12px', color: 'var(--text2)' }}>· Costo: {fmt(s.unit_cost)}</span>}
                  </div>
                </div>
                <button
                  data-testid={`writeoff-btn-${s.id}`}
                  onClick={() => openWriteoff(s)}
                  disabled={s.quantity === 0}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, background: s.quantity === 0 ? 'var(--surface2)' : 'rgba(255,82,82,0.1)', border: `1px solid ${s.quantity === 0 ? 'var(--border)' : '#ff5252'}`, color: s.quantity === 0 ? 'var(--text2)' : '#ff5252', fontSize: '13px', fontWeight: 700, cursor: s.quantity === 0 ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                >
                  <PackageX size={15} /> Dar de Baja
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ── WRITE-OFF MODAL ── */}
      {showWriteoffModal && writeoffTarget && (
        <div className="modal-overlay" onClick={() => setShowWriteoffModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Baja de Inventario</h3>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: '15px', fontWeight: 700 }}>{writeoffTarget.description}</div>
              <div style={{ fontSize: '13px', color: 'var(--text2)', marginTop: 4 }}>Stock actual: <strong>{writeoffTarget.quantity}</strong> unidades</div>
            </div>
            <div className="form-group">
              <label className="label">Cantidad a dar de baja *</label>
              <input
                className="input"
                type="number"
                min="1"
                max={writeoffTarget.quantity}
                data-testid="writeoff-quantity-input"
                value={writeoffForm.quantity}
                onChange={e => setWriteoffForm(f => ({ ...f, quantity: e.target.value }))}
                placeholder={`Máximo: ${writeoffTarget.quantity}`}
              />
            </div>
            <div className="form-group">
              <label className="label">Motivo *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {WRITEOFF_REASONS.map(r => (
                  <button
                    key={r}
                    type="button"
                    data-testid={`reason-${r.replace(/\s/g, '-').toLowerCase()}`}
                    onClick={() => setWriteoffForm(f => ({ ...f, reason: r }))}
                    style={{ padding: '10px 14px', borderRadius: 10, border: `2px solid ${writeoffForm.reason === r ? '#ff5252' : 'var(--border)'}`, background: writeoffForm.reason === r ? 'rgba(255,82,82,0.08)' : 'var(--surface)', color: writeoffForm.reason === r ? '#ff5252' : 'var(--text)', fontWeight: writeoffForm.reason === r ? 700 : 400, cursor: 'pointer', textAlign: 'left', fontSize: '14px' }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowWriteoffModal(false)}>Cancelar</button>
              <button
                data-testid="confirm-writeoff-btn"
                className="btn-danger"
                onClick={confirmWriteoff}
                disabled={writingOff}
                style={{ flex: 1 }}
              >
                {writingOff ? 'Registrando...' : 'Confirmar Baja'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ margin: 0 }}>Nueva Compra</h3>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}>
                <X size={22} />
              </button>
            </div>
            <form onSubmit={savePurchase}>
              {/* Provider */}
              <div className="form-group">
                <label className="label">Proveedor *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="input"
                    data-testid="purchase-provider-select"
                    value={purchaseForm.provider_id}
                    onChange={e => setPurchaseForm(f => ({ ...f, provider_id: e.target.value }))}
                    style={{ flex: 1 }}
                  >
                    <option value="">Seleccionar proveedor</option>
                    {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ padding: '8px 12px', whiteSpace: 'nowrap', fontSize: '12px' }}
                    onClick={() => setShowNewProviderInline(v => !v)}
                  >
                    + Nuevo
                  </button>
                </div>
                {showNewProviderInline && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                    <input
                      className="input"
                      placeholder="Nombre del proveedor"
                      data-testid="inline-provider-name"
                      value={formNewProvider}
                      onChange={e => setFormNewProvider(e.target.value)}
                      style={{ flex: 1, minWidth: 0, border: '1px solid var(--accent)' }}
                    />
                    <button
                      type="button"
                      data-testid="inline-provider-create"
                      onClick={createInlineProvider}
                      disabled={savingProvider}
                      style={{
                        padding: '10px 16px', borderRadius: 10, flexShrink: 0,
                        background: 'linear-gradient(145deg, var(--gradient-end), var(--gradient-start))',
                        color: '#000', fontWeight: 700, fontSize: '13px',
                        border: 'none', cursor: 'pointer', width: 'auto',
                        boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.25), 0 3px 8px rgba(93,230,200,0.3)',
                      }}
                    >
                      {savingProvider ? '...' : 'Crear'}
                    </button>
                  </div>
                )}
              </div>

              {/* Items */}
              <div className="form-group">
                <label className="label">Ítems de la compra *</label>
                {purchaseForm.items.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                    <input
                      className="input"
                      placeholder="Descripción"
                      value={item.description}
                      onChange={e => updateItem(i, 'description', e.target.value)}
                      style={{ flex: 2 }}
                      data-testid={`purchase-item-desc-${i}`}
                    />
                    <input
                      className="input"
                      type="number"
                      placeholder="Cant."
                      min="0"
                      step="0.01"
                      value={item.quantity}
                      onChange={e => updateItem(i, 'quantity', e.target.value)}
                      style={{ flex: 1, minWidth: 0 }}
                      data-testid={`purchase-item-qty-${i}`}
                    />
                    <input
                      className="input"
                      type="number"
                      placeholder="$/u"
                      min="0"
                      step="0.01"
                      value={item.unit_cost}
                      onChange={e => updateItem(i, 'unit_cost', e.target.value)}
                      style={{ flex: 1, minWidth: 0 }}
                      data-testid={`purchase-item-cost-${i}`}
                    />
                    {purchaseForm.items.length > 1 && (
                      <button type="button" onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 4 }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ width: '100%', marginTop: 4, fontSize: '13px' }}
                  onClick={addItem}
                  data-testid="add-purchase-item-btn"
                >
                  <Plus size={14} style={{ marginRight: 4 }} /> Agregar ítem
                </button>
              </div>

              {/* Shipping cost field */}
              <div className="form-group">
                <label className="label">Gasto de envío (opcional)</label>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={purchaseForm.shipping_cost}
                  onChange={e => setPurchaseForm(f => ({ ...f, shipping_cost: e.target.value }))}
                  data-testid="purchase-shipping-cost"
                />
              </div>

              {/* Total */}
              {purchaseTotal > 0 && (
                <div style={{ textAlign: 'center', padding: '10px', background: 'var(--surface)', borderRadius: 10, marginBottom: 14 }}>
                  <div style={{ fontSize: '11px', color: 'var(--text2)', marginBottom: 2 }}>Total de la compra</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#27AE60' }}>{fmt(purchaseTotal)}</div>
                  {parseFloat(purchaseForm.shipping_cost) > 0 && (
                    <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 4 }}>
                      Productos: {fmt(purchaseTotal - (parseFloat(purchaseForm.shipping_cost) || 0))} + Envío: {fmt(parseFloat(purchaseForm.shipping_cost) || 0)}
                    </div>
                  )}
                  {parseFloat(purchaseForm.shipping_cost) > 0 && (() => {
                    const totalQty = purchaseForm.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0);
                    if (totalQty <= 0) return null;
                    const shipPerUnit = (parseFloat(purchaseForm.shipping_cost) || 0) / totalQty;
                    return (
                      <div style={{ fontSize: '12px', color: '#FF9800', marginTop: 4, fontWeight: 600 }}>
                        Envío por unidad: {fmt(shipPerUnit)} (se suma al costo de cada producto en el stock)
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Payment status */}
              <div className="form-group">
                <label className="label">Estado de pago *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { key: 'paid', label: 'Pagado', color: '#4CAF50' },
                    { key: 'partial', label: 'Parcial', color: '#FF9800' },
                    { key: 'pending', label: 'Pendiente', color: '#FF5252' },
                  ].map(s => (
                    <button
                      key={s.key}
                      type="button"
                      data-testid={`purchase-status-${s.key}`}
                      onClick={() => setPurchaseForm(f => ({ ...f, status: s.key }))}
                      style={{
                        flex: 1,
                        padding: '10px 4px',
                        borderRadius: 10,
                        border: `2px solid ${purchaseForm.status === s.key ? s.color : 'var(--border)'}`,
                        background: purchaseForm.status === s.key ? `${s.color}20` : 'var(--surface)',
                        color: purchaseForm.status === s.key ? s.color : 'var(--text2)',
                        fontWeight: purchaseForm.status === s.key ? 700 : 500,
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                {purchaseForm.status === 'partial' && (
                  <div style={{ marginTop: 10 }}>
                    <label className="label">Monto ya pagado</label>
                    <input
                      className="input"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={purchaseForm.paid_amount}
                      onChange={e => setPurchaseForm(f => ({ ...f, paid_amount: e.target.value }))}
                      data-testid="purchase-paid-amount"
                    />
                  </div>
                )}
                {/* Payment method — required when paid or partial */}
                {(purchaseForm.status === 'paid' || purchaseForm.status === 'partial') && (
                  <div style={{ marginTop: 12 }}>
                    <label className="label">Forma de pago *</label>
                    <div className="payment-row">
                      <button
                        type="button"
                        data-testid="create-pay-efectivo"
                        className={`payment-btn ${purchaseForm.payment_method === 'efectivo' ? 'active' : ''}`}
                        onClick={() => setPurchaseForm(f => ({ ...f, payment_method: 'efectivo' }))}
                      >
                        <Banknote size={16} /> Efectivo
                      </button>
                      <button
                        type="button"
                        data-testid="create-pay-transferencia"
                        className={`payment-btn ${purchaseForm.payment_method === 'transferencia' ? 'active' : ''}`}
                        onClick={() => setPurchaseForm(f => ({ ...f, payment_method: 'transferencia' }))}
                      >
                        <Smartphone size={16} /> Transferencia
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="label">Notas (opcional)</label>
                <input
                  className="input"
                  placeholder="Observaciones de la compra..."
                  value={purchaseForm.notes}
                  onChange={e => setPurchaseForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>

              {/* Invoice Photos */}
              <div className="form-group">
                <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Camera size={14} /> Fotos de factura / comprobante (opcional, máx. 5)
                </label>
                <input
                  type="file"
                  ref={photoInputRef}
                  accept="image/*"
                  multiple
                  onChange={addPurchasePhoto}
                  style={{ display: 'none' }}
                  data-testid="purchase-photo-input"
                />
                {purchaseForm.photos.length < 5 && (
                  <button
                    type="button"
                    className="btn-secondary"
                    data-testid="add-purchase-photo-btn"
                    onClick={() => photoInputRef.current?.click()}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: purchaseForm.photos.length > 0 ? 10 : 0 }}
                  >
                    <Image size={15} /> Cargar fotos de factura
                  </button>
                )}
                {purchaseForm.photos.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {purchaseForm.photos.map((photo, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={photo} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                        <button
                          type="button"
                          onClick={() => removePurchasePhoto(i)}
                          style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ background: 'rgba(39,174,96,0.08)', border: '1px solid rgba(39,174,96,0.2)', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: '12px', color: 'var(--text2)' }}>
                Los ítems se agregarán automáticamente al Stock con el costo unitario registrado. Las ganancias de venta se calcularán usando ese costo.
              </div>

              <div className="modal-buttons">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" data-testid="save-purchase-btn" disabled={saving}>
                  {saving ? 'Guardando...' : 'Registrar Compra'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── PAY MODAL ── */}
      {showPayModal && payTarget && (
        <div className="modal-overlay" onClick={() => setShowPayModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Registrar Pago</h3>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: '14px', color: 'var(--text2)', marginBottom: 4 }}>{payTarget.provider_name}</div>
              <div style={{ fontSize: '12px', color: 'var(--text2)' }}>Deuda pendiente</div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#ff5252' }}>{fmt(payTarget.remaining_debt)}</div>
            </div>
            <div className="form-group">
              <label className="label">Monto a pagar</label>
              <input
                className="input"
                type="number"
                step="0.01"
                value={payForm.amount}
                onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                data-testid="payment-amount-input"
              />
            </div>
            <div className="form-group">
              <label className="label">Forma de pago *</label>
              <div className="payment-row">
                <button
                  type="button"
                  data-testid="pay-efectivo"
                  className={`payment-btn ${payForm.payment_method === 'efectivo' ? 'active' : ''}`}
                  onClick={() => { setPayForm(f => ({ ...f, payment_method: 'efectivo' })); setPayAlert(''); }}
                >
                  <Banknote size={16} /> Efectivo
                </button>
                <button
                  type="button"
                  data-testid="pay-transferencia"
                  className={`payment-btn ${payForm.payment_method === 'transferencia' ? 'active' : ''}`}
                  onClick={() => { setPayForm(f => ({ ...f, payment_method: 'transferencia' })); setPayAlert(''); }}
                >
                  <Smartphone size={16} /> Transferencia
                </button>
              </div>
            </div>
            {payAlert && (
              <div style={{ color: '#ff5252', fontSize: '12px', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={14} /> {payAlert}
              </div>
            )}
            {/* Comprobante photo for payment */}
            <div className="form-group">
              <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Camera size={14} /> Foto del comprobante (opcional)
              </label>
              <input
                type="file"
                ref={payPhotoInputRef}
                accept="image/*"
                onChange={addPayPhoto}
                style={{ display: 'none' }}
                data-testid="pay-photo-input"
              />
              {!payForm.photo ? (
                <button
                  type="button"
                  className="btn-secondary"
                  data-testid="add-pay-photo-btn"
                  onClick={() => payPhotoInputRef.current?.click()}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  <Camera size={15} /> Adjuntar comprobante
                </button>
              ) : (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={payForm.photo} alt="Comprobante" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                  <button
                    type="button"
                    onClick={() => setPayForm(f => ({ ...f, photo: null }))}
                    style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >×</button>
                </div>
              )}
            </div>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowPayModal(false)}>Cancelar</button>
              <button
                data-testid="confirm-payment-btn"
                className="btn-primary"
                onClick={confirmPayment}
                disabled={paying}
                style={{ flex: 1, background: 'linear-gradient(135deg, #4CAF50, #66BB6A)' }}
              >
                {paying ? 'Registrando...' : 'Confirmar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PROVIDER MODAL ── */}
      {showProviderModal && (
        <div className="modal-overlay" onClick={() => setShowProviderModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">{editingProvider ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h3>
            <form onSubmit={saveProvider}>
              <div className="form-group">
                <label className="label">Nombre *</label>
                <input className="input" data-testid="provider-name-input" value={providerForm.name} onChange={e => setProviderForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="label">Teléfono</label>
                <input className="input" value={providerForm.phone} onChange={e => setProviderForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="label">Notas</label>
                <input className="input" value={providerForm.notes} onChange={e => setProviderForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="modal-buttons">
                <button type="button" className="btn-secondary" onClick={() => setShowProviderModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" data-testid="save-provider-btn" disabled={savingProvider}>
                  {savingProvider ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DELETE PROVIDER CONFIRM ── */}
      {deleteProvider && (
        <div className="modal-overlay" onClick={() => setDeleteProvider(null)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Eliminar Proveedor</h3>
            <p style={{ textAlign: 'center', color: 'var(--text2)', marginBottom: 20 }}>
              ¿Eliminar <strong style={{ color: 'var(--text)' }}>{deleteProvider.name}</strong>?<br />Esta acción no se puede deshacer.
            </p>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setDeleteProvider(null)}>Cancelar</button>
              <button className="btn-danger" onClick={confirmDeleteProvider} style={{ flex: 1 }}>Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE PURCHASE CONFIRM ── */}
      {deletePurchase && (
        <div className="modal-overlay" onClick={() => setDeletePurchase(null)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Eliminar Compra</h3>
            <p style={{ textAlign: 'center', color: 'var(--text2)', marginBottom: 20 }}>
              ¿Eliminar la compra de <strong style={{ color: 'var(--text)' }}>{deletePurchase.provider_name}</strong>?<br />Esta acción no se puede deshacer.
            </p>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setDeletePurchase(null)}>Cancelar</button>
              <button className="btn-danger" onClick={confirmDeletePurchase} disabled={deleting} style={{ flex: 1 }}>
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── VENDEDOR: UNIFIED PRODUCT LOAD WIZARD ── */}
      {showVendorWizard && (
        <VendorProductWizard
          user={user}
          providers={providers}
          onClose={() => setShowVendorWizard(false)}
          onProviderCreated={(p) => setProviders(prev => [...prev, p])}
          onSaved={() => { setShowVendorWizard(false); load(); }}
        />
      )}

      {/* ── IMAGE LIGHTBOX ── */}
      {viewingPhoto && (
        <div
          data-testid="photo-lightbox"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.96)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setViewingPhoto(null)}
        >
          <button
            onClick={() => setViewingPhoto(null)}
            style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3001 }}
          >
            <X size={22} color="#fff" />
          </button>
          <img
            src={viewingPhoto}
            alt="Vista completa"
            style={{ maxWidth: '100%', maxHeight: '88vh', objectFit: 'contain', borderRadius: 8 }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
